import { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';

const API = import.meta.env.VITE_API_URL ?? '';

/**
 * Public Inspector View — accessed via /inspect/{token}.
 * 3 tabs: Schlagkartei, GLÖZ, PSM protocols.
 * No auth required — token-based access.
 */
export default function InspectorView({ inspectorToken }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState('schlagkartei');
  const [tabData, setTabData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/inspect/${inspectorToken}`);
        if (res.status === 410) { setError('expired'); return; }
        if (res.status === 403) { setError('revoked'); return; }
        if (!res.ok) { setError('notfound'); return; }
        setOverview(await res.json());
      } catch { setError('network'); }
      finally { setLoading(false); }
    })();
  }, [inspectorToken]);

  useEffect(() => {
    if (!overview) return;
    setTabData(null);
    (async () => {
      try {
        const res = await fetch(`${API}/api/v1/aif/inspect/${inspectorToken}/${tab}`);
        if (res.ok) setTabData(await res.json());
      } catch { /* ignore */ }
    })();
  }, [tab, overview]);

  if (loading) return <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)]"><div className="w-8 h-8 border-3 border-gray-200 border-t-green-600 rounded-full animate-spin" /></div>;

  if (error) {
    const msgs = { expired: 'Token abgelaufen', revoked: 'Token widerrufen', notfound: 'Token ungültig', network: 'Verbindungsfehler' };
    return <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--aif-bg-tertiary)]"><div className="text-center"><div className="text-4xl mb-3">{error === 'expired' ? '\u23F0' : '\u26D4'}</div><p className="text-sm text-[var(--aif-text-secondary)]">{msgs[error]}</p></div></div>;
  }

  if (!overview) return null;

  const TABS = [
    { key: 'schlagkartei', label: t('compliance.schlagkartei') },
    { key: 'gloez', label: t('compliance.gloez') },
    { key: 'psm', label: t('compliance.psm') },
  ];

  const STATUS_BADGE = { konform: 'bg-green-100 text-green-700', warnung: 'bg-amber-100 text-amber-700', verletzung: 'bg-red-100 text-red-700', keine_daten: 'bg-gray-100 text-[var(--aif-text-muted)]' };
  const STATUS_ICON = { konform: '\u2705', warnung: '\u26A0\uFE0F', verletzung: '\u274C', keine_daten: '\u2753' };

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-tertiary)] flex flex-col">
      {/* Header */}
      <div className="bg-green-700 text-white px-6 py-4">
        <div className="text-lg font-bold flex items-center gap-2"><span>{'\uD83C\uDF3E'}</span> AI Farm — {t('compliance.inspection')}</div>
        <div className="text-sm opacity-90">{overview.farm?.name || 'Betrieb'} &middot; {overview.total_ha} ha &middot; {overview.fields_count} {t('compliance.fields')}</div>
        <div className="text-xs opacity-75 mt-1">{t('compliance.period')}: {overview.data_from} — {overview.data_to}</div>
      </div>

      {/* Tabs */}
      <div className="flex border-b bg-[var(--aif-bg-surface)]">
        {TABS.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)} className={`flex-1 py-3 text-sm font-medium border-b-2 transition ${tab === tb.key ? 'border-green-600 text-green-700' : 'border-transparent text-[var(--aif-text-muted)]'}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {!tabData ? (
          <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-gray-200 border-t-green-600 rounded-full animate-spin" /></div>
        ) : tab === 'schlagkartei' ? (
          (tabData.fields || []).map((f, i) => (
            <details key={i} className="bg-[var(--aif-bg-surface)] border rounded-xl overflow-hidden">
              <summary className="px-4 py-3 cursor-pointer flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--aif-text-primary)]">{f.name} ({f.area_ha} ha)</span>
                <span className="text-xs text-[var(--aif-text-muted)]">{f.activities?.length || 0} {t('compliance.entries')}</span>
              </summary>
              <div className="border-t divide-y">
                {(f.activities || []).map((a, j) => (
                  <div key={j} className="px-4 py-2 text-xs text-[var(--aif-text-secondary)]">
                    <div className="flex justify-between"><span className="font-medium">{a.type}</span><span>{a.date}</span></div>
                    {a.operator && <div>{t('compliance.operator')}: {a.operator}</div>}
                    {a.machine && <div>{t('compliance.machine')}: {a.machine}</div>}
                  </div>
                ))}
                {(!f.activities || f.activities.length === 0) && <div className="px-4 py-2 text-xs text-[var(--aif-text-tertiary)]">{t('compliance.noEntries')}</div>}
              </div>
            </details>
          ))
        ) : tab === 'gloez' ? (
          (tabData.checks || []).map((c, i) => (
            <div key={i} className="bg-[var(--aif-bg-surface)] border rounded-xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-[var(--aif-text-primary)]">{c.standard}: {c.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[c.status] || ''}`}>
                  {STATUS_ICON[c.status]} {c.status}
                </span>
              </div>
              <p className="text-xs text-[var(--aif-text-secondary)]">{c.detail}</p>
            </div>
          ))
        ) : tab === 'psm' ? (
          (tabData.applications || []).length === 0 ? (
            <div className="text-center py-8 text-sm text-[var(--aif-text-tertiary)]">{t('compliance.noPSM')}</div>
          ) : (tabData.applications || []).map((a, i) => (
            <div key={i} className="bg-[var(--aif-bg-surface)] border rounded-xl p-4 space-y-1">
              <div className="flex justify-between text-sm"><span className="font-medium text-[var(--aif-text-primary)]">{a.field}</span><span className="text-[var(--aif-text-muted)]">{a.date}</span></div>
              {a.area_ha && <div className="text-xs text-[var(--aif-text-secondary)]">{a.area_ha} ha</div>}
              {a.operator && <div className="text-xs text-[var(--aif-text-secondary)]">{t('compliance.operator')}: {a.operator}</div>}
              {a.weather && <div className="text-xs text-[var(--aif-text-muted)]">{t('compliance.weather')}: {a.weather}</div>}
            </div>
          ))
        ) : null}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 text-center text-xs text-[var(--aif-text-tertiary)] border-t bg-[var(--aif-bg-surface)]">
        {t('share.validUntil')}: {new Date(overview.expires_at || overview.data_to).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        <div className="mt-1">Powered by AI Farm</div>
      </div>
    </div>
  );
}
